import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/Cards';
import { Icon } from '@/components/Icons';
import { ChevronRightFilled } from '@/components/Icons/ChevronRightFilled';
import { Notification } from '@/components/Notification';
import { PersonType } from '@/types/user';
import { getRoleLabel, personTypeIcons } from '@/utils/person';
import { joinNonEmpty } from '@/utils/text';
import { tv } from '@/utils/tv';

const avatar = tv({
  base: 'size-12 shrink-0',
  variants: {
    isInactive: {
      true: '[&_.fill-purple-soft]:fill-on-primary-surface [&_.fill-primary]:fill-muted-foreground',
    },
  },
});

interface Props {
  firstName: string;
  lastName: string;
  className?: string;
  schoolName?: string;
  personType: PersonType;
  notificationCount?: number;
  isInactive?: boolean;
  onClick?: () => void;
}

export const ProfileCard: React.FC<Props> = ({
  firstName,
  lastName,
  className,
  schoolName,
  personType,
  notificationCount,
  isInactive = false,
  onClick,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      onClick={onClick}
      className="!flex-row !items-center !gap-4 !py-3 !pr-3 !pl-4 bg-popover transition-colors duration-200 ease-out md:hover:bg-hover"
    >
      <Icon
        icon={personTypeIcons[personType]}
        className={avatar({ isInactive })}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-col">
          <p className="truncate text-[15px] font-medium leading-5 text-foreground md:text-subhead-14">
            {firstName} {lastName}
          </p>
          {(className || schoolName) && (
            <p className="text-caption-12 truncate text-foreground">
              {joinNonEmpty([className, schoolName])}
            </p>
          )}
        </div>
        <p className="text-caption-12 truncate text-subtle-foreground">
          {t('profile.roleLabel')}: {getRoleLabel(personType)}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!!notificationCount && notificationCount > 0 && (
          <Notification count={notificationCount} />
        )}
        <ChevronRightFilled className="size-4 text-muted-foreground" />
      </div>
    </Card>
  );
};
